/* eslint-disable react/jsx-no-bind */
/* @flow */
import * as React from 'react';
import { translate, type TFunction } from 'react-i18next';
import { type Dispatch } from 'redux';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { useFormik } from 'formik';
import { Columns, Column, Stack } from '@locus-taxy/neo-ui';
import noop from 'lodash/noop';

import {
    PopoverPlaceEnum,
    PopoverTriggerEnum
} from 'common/components/core/popover/popOverFactory';
import {
    ButtonTypes,
    ButtonWithPopover,
    Button
} from 'common/components/core/button';
import { dashboardColors } from 'common/enums/ColorEnum';
import { ConfirmationDialog } from 'common/components/core/dialog/ConfirmationDialog';
import { ErrorMessage } from 'common/components/core/input/ErrorMessage';
import {
    navigationPromptFactory,
    withRouter,
    type RouterHistory,
    type Location
} from 'common/components/core/router';
import PasswordField from 'common/components/core/password/PasswordField';
import AlertDialog from 'common/components/core/dialog/AlertDialog';
import MinimalPersonnelFetchHOC from './MinimalPersonnelFetchHOC';
import { PersonnelIdDisplay } from './PersonnelIdDisplay';
import PasswordStrength, { usePasswordStrength } from './PasswordStrength';

import { RegExUtil } from 'common/utils/RegExUtil';
import { minimalPersonnelSelector } from 'common/redux/selectors/sessionSelector';
import { MinimalPersonnel } from 'common/models/internal/MinimalPersonnel';
import { ApiError } from 'common/modules/api';
import { AuthenticationActions } from 'common/modules/auth';
import { updatePersonnelPassword } from 'common/redux/actions/session';
import changePasswordIds from 'common/tests/changePassword/changePasswordIds.json';

import theme from './ChangePassword.scss';
type OwnProps = {
    username: string,
    allowNavigation?: boolean,
    showTitle?: boolean,
    allowCancel?: boolean,
    onCancel?: () => void,
    renderHeader?: () => React.Node,
    onUpdatePasswordSuccess: () => void
};

type Props = {
    t: TFunction,
    location: Location,
    history: RouterHistory,
    minimalPersonnel: MinimalPersonnel,
    dispatch: Dispatch<$FixMe>,
    ...OwnProps
};

const NavigationPrompt = navigationPromptFactory({
    Prompt: AlertDialog
});

const ChangePasswordBase = ({
    allowNavigation = false,
    allowCancel = true,
    t,
    onCancel,
    showTitle,
    renderHeader,
    minimalPersonnel,
    dispatch,
    onUpdatePasswordSuccess
}: Props): React.Node => {
    const [passwordApiError, setPasswordApiError] = React.useState(null);
    const [isOpenConfirmDialog, setIsOpenConfirmationDialog] = React.useState(
        false
    );

    const form = useFormik({
        initialValues: {
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        },
        validate(values) {
            const errors = {};
            const { confirmPassword, newPassword, currentPassword } = values;

            if (!currentPassword) {
                errors.currentPassword = t('errorMessage.required');
            }

            // validate newPassword
            if (newPassword) {
                if (!RegExUtil.isASCII(newPassword)) {
                    errors.newPassword = t('errorMessage.nonASCII');
                } else if (newPassword === currentPassword) {
                    errors.newPassword = t('errorMessage.newPassword');
                }
            } else {
                errors.newPassword = t('errorMessage.required');
            }

            // validate confirmPassword
            if (confirmPassword) {
                if (confirmPassword !== newPassword) {
                    errors.confirmPassword = t('errorMessage.confirmPassword');
                }
            } else {
                errors.confirmPassword = t('errorMessage.required');
            }

            return errors;
        },
        onSubmit() {
            setPasswordApiError(null);
            return updatePersonnelPassword(
                minimalPersonnel.clientId,
                minimalPersonnel.personnelId,
                {
                    existingPassword: form.values.currentPassword,
                    newPassword: form.values.newPassword
                }
            )()
                .then(() => {
                    dispatch(
                        AuthenticationActions.localLogoutOnPasswordChange()
                    );
                    onUpdatePasswordSuccess();
                })
                .catch(error => {
                    if (error instanceof ApiError) {
                        setPasswordApiError(error.message);
                    } else {
                        throw error;
                    }
                });
        }
    });

    const { newPassword } = form.values;
    const strength = usePasswordStrength({
        password: newPassword,
        minimalPersonnel
    });

    const handleCancel = () => {
        if (form.dirty) {
            setIsOpenConfirmationDialog(true);
            return;
        }
        onCancel?.();
    };

    const renderNavigationPrompt = () => {
        const { pathname, search } = location;
        const whiteListedPathnames = allowNavigation
            ? [`${pathname}${search}`]
            : [];

        return (
            <NavigationPrompt
                when={!allowNavigation && form.dirty}
                cancelLabel={t('translations:stay')}
                confirmLabel={t('translations:leave')}
                whiteListedPathnames={whiteListedPathnames}
                title={t('translations:leaveThisPage')}
            >
                <span>{t('translations:unsavedChangesWillBeLost')}</span>
            </NavigationPrompt>
        );
    };

    const renderPasswordField = ({
        id,
        testId,
        autocomplete,
        autoFocus = false
    }) => {
        return (
            <PasswordField
                label={t(id)}
                onChange={value => {
                    form.handleChange(id)(value);
                    form.setTouched(
                        { ...form.touched, [id]: Boolean(value) },
                        false
                    );
                }}
                errorMessage={form.touched[id] ? form.errors[id] : null}
                value={form.values[id]}
                autocomplete={autocomplete}
                autoFocus={autoFocus}
                testId={testId}
            />
        );
    };

    const renderForm = () => {
        const isFormValid = form.isValid && strength.verified;
        const disableSubmit = !isFormValid || form.isSubmitting;

        return (
            <form onSubmit={disableSubmit ? noop : form.handleSubmit}>
                <Stack space="large">
                    {renderPasswordField({
                        id: 'currentPassword',
                        autoFocus: true,
                        autocomplete: 'current-password',
                        testId: changePasswordIds.currentPassword
                    })}
                    {renderPasswordField({
                        id: 'newPassword',
                        autocomplete: 'new-password',
                        testId: changePasswordIds.newPassword
                    })}
                    {renderPasswordField({
                        id: 'confirmPassword',
                        autocomplete: 'new-password',
                        testId: changePasswordIds.confirmPassword
                    })}

                    {/* Api Error */}
                    {passwordApiError && (
                        <ErrorMessage
                            testId={
                                changePasswordIds.changePasswordActionStatus
                                    .failure
                            }
                        >
                            {passwordApiError}
                        </ErrorMessage>
                    )}

                    {/* Buttons */}
                    <div className={theme.submitButtons}>
                        {allowCancel ? (
                            <Button
                                uppercase
                                semibold
                                theme={theme}
                                onClick={handleCancel}
                                className={theme.cancelBtn}
                                buttonType={ButtonTypes.Text}
                                label={t('translations:cancel')}
                                testId={changePasswordIds.buttons.cancel}
                            />
                        ) : null}
                        <ButtonWithPopover
                            label={t('changePassword')}
                            type="submit"
                            uppercase
                            semibold
                            theme={theme}
                            disabled={disableSubmit}
                            isLoading={form.isSubmitting}
                            color={dashboardColors.darkSkyBlue.name}
                            className={theme.submitBtn}
                            place={PopoverPlaceEnum.Above}
                            buttonType={ButtonTypes.Solid}
                            triggerPopoverOn={PopoverTriggerEnum.Hover}
                            isVisible={disableSubmit}
                            popOverContent={
                                !isFormValid &&
                                t('errorMessage.invalidPassword')
                            }
                            testId={changePasswordIds.buttons.changePassword}
                        />
                    </div>

                    {isOpenConfirmDialog ? (
                        <ConfirmationDialog
                            title={t('discardChanges')}
                            theme={theme}
                            onAccept={() => {
                                onCancel?.();
                                setIsOpenConfirmationDialog(false);
                            }}
                            onCancel={() => {
                                setIsOpenConfirmationDialog(false);
                            }}
                            acceptActionLabel={t('translations:yes')}
                            cancelActionLabel={t('translations:no')}
                            active
                            innerText={t('discardChangesMessage')}
                        />
                    ) : null}

                    {renderNavigationPrompt()}
                </Stack>
            </form>
        );
    };

    const renderPasswordSection = () => {
        return (
            <Stack space="large">
                {showTitle ? (
                    <div className={theme.passwordTitle}>
                        {t('changePassword')}
                    </div>
                ) : null}
                <Columns space="large">
                    <Column width="1/2">{renderForm()}</Column>
                    <Column width="1/3">
                        <PasswordStrength result={strength} />
                    </Column>
                </Columns>
            </Stack>
        );
    };

    return (
        <Columns>
            <Column>
                <Stack space="small">
                    {/* User name section */}
                    {renderHeader ? renderHeader() : null}
                    {/* change password view */}
                    <>
                        <PersonnelIdDisplay
                            clientId={minimalPersonnel.clientId}
                            personnelId={minimalPersonnel.personnelId}
                        />
                        {renderPasswordSection()}
                    </>
                </Stack>
            </Column>
        </Columns>
    );
};

export default (compose(
    MinimalPersonnelFetchHOC,
    withRouter,
    connect(state => {
        return {
            minimalPersonnel: minimalPersonnelSelector(state)
        };
    }),
    translate('settings')
)(ChangePasswordBase): React.AbstractComponent<OwnProps>);
